// A cursor-following tooltip, for dense lists where the CSS `.cm-tip` bubble (anchored, animated)
// covers neighbouring rows and appears too late. Shows at once, tracks the pointer, and flips at
// edges. One shared element, created on first hover.

import type React from 'react';

let el: HTMLDivElement | null = null;

// Offset from the cursor, below-right so the pointer never covers the text.
const OFFSET_X = 14;
const OFFSET_Y = 18;
const MARGIN = 8; // keep this far off the viewport edge

function ensure(): HTMLDivElement {
  if (el == null) {
    el = document.createElement('div');
    el.className = 'cm-cursor-tip';
    el.setAttribute('role', 'tooltip');
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);
    // Scrolling moves content without a mouseleave, stranding the bubble; capture phase so inner
    // scrollers count too.
    window.addEventListener('scroll', hideCursorTip, true);
  }
  return el;
}

function place(node: HTMLDivElement, x: number, y: number) {
  // Measure after the text is set; the width decides the side.
  const rect = node.getBoundingClientRect();
  let left = x + OFFSET_X;
  let top = y + OFFSET_Y;
  // Flip rather than clamp: pinned to the edge, the tooltip would sit under the pointer.
  if (left + rect.width + MARGIN > window.innerWidth) {
    left = x - OFFSET_X - rect.width;
  }
  if (top + rect.height + MARGIN > window.innerHeight) {
    top = y - OFFSET_Y - rect.height;
  }
  node.style.left = `${Math.max(MARGIN, left)}px`;
  node.style.top = `${Math.max(MARGIN, top)}px`;
}

export function showCursorTip(text: string, x: number, y: number): void {
  const node = ensure();
  if (node.textContent !== text) {
    node.textContent = text;
  }
  node.classList.add('is-visible');
  place(node, x, y);
}

export function moveCursorTip(x: number, y: number): void {
  if (el == null || !el.classList.contains('is-visible')) return;
  place(el, x, y);
}

export function hideCursorTip(): void {
  if (el == null) return;
  el.classList.remove('is-visible');
}

// Handlers to spread onto an element: <button {...cursorTipProps(text)}>. Touch never fires them, so
// badges also keep an aria-label.
export function cursorTipProps(text: string): {
  onMouseEnter: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
} {
  return {
    onMouseEnter: (e) => showCursorTip(text, e.clientX, e.clientY),
    onMouseMove: (e) => moveCursorTip(e.clientX, e.clientY),
    onMouseLeave: hideCursorTip,
  };
}
